import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Container,
  Alert,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  ContentCopy,
  Download,
  Share,
  CurrencyBitcoin
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas as QRCode } from 'qrcode.react';

const ReceivePage = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Your Bitcoin wallet address
  const walletAddress = "bc1qtctdnafccfe7wcfcqe9pmverr7w2m3drek7unh";
  const network = "Bitcoin (BTC)";

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById('qr-code');
    if (canvas) {
      const pngUrl = canvas
        .toDataURL('image/png')
        .replace('image/png', 'image/octet-stream');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `gantryfx_btc_qr_${walletAddress.slice(0, 8)}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'GantryFX Bitcoin Wallet',
          text: `My Bitcoin wallet address: ${walletAddress}`,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      handleCopyAddress();
    }
  };

  const formatAddress = (address) => {
    if (address.length <= 16) return address;
    return `${address.slice(0, 10)}...${address.slice(-10)}`;
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#0A0A0A',
      pb: 4
    }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0,
          bgcolor: '#0A0A0A',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          px: 2,
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#fff' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00FF87 0%, #60EFFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Receive Bitcoin
          </Typography>
        </Box>
      </Paper>

      <Container maxWidth="md" sx={{ px: 2, py: 3 }}>
        {/* Instructions */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 3
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CurrencyBitcoin sx={{ color: '#F7931A' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Send only BTC to this address
              </Typography>
            </Box>
            <Alert 
              severity="info" 
              sx={{ 
                bgcolor: 'rgba(96,239,255,0.1)', 
                color: '#60EFFF',
                '& .MuiAlert-icon': { color: '#60EFFF' }
              }}
            >
              Only send Bitcoin (BTC) on the Bitcoin network. Sending other cryptocurrencies may result in permanent loss.
            </Alert>
          </CardContent>
        </Card>

        {/* QR Code */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 3,
          textAlign: 'center'
        }}>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ 
              display: 'inline-block',
              bgcolor: 'white',
              p: 2,
              borderRadius: 2,
              mb: 2
            }}>
              <QRCode
                id="qr-code"
                value={`bitcoin:${walletAddress}`}
                size={200}
                bgColor="#FFFFFF"
                fgColor="#000000"
                level="H"
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
              Scan with any Bitcoin wallet
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadQR}
                sx={{
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#00FF87', color: '#00FF87' }
                }}
              >
                Save QR
              </Button>
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={handleShare}
                sx={{
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#00FF87', color: '#00FF87' }
                }}
              >
                Share
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Wallet Address */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 3
        }}>
          <CardContent>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              Bitcoin Wallet Address
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              bgcolor: '#1A1A1A',
              p: 2,
              borderRadius: 2,
              mb: 1
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#fff',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  wordBreak: 'break-all'
                }}
              >
                {walletAddress}
              </Typography>
              <IconButton onClick={handleCopyAddress} sx={{ color: '#00FF87' }}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', textAlign: 'center', mt: 1 }}>
              {formatAddress(walletAddress)}
            </Typography>
          </CardContent>
        </Card>

        {/* Network Info */}
        <Card sx={{ 
          borderRadius: 3,
          bgcolor: '#111',
          border: '1px solid rgba(255,255,255,0.05)'
        }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#fff', mb: 2 }}>
              Network Information
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Network:</Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>{network}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Address Type:</Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>Bech32 (Native SegWit)</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Minimum Deposit:</Typography>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>0.0001 BTC</Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ bgcolor: '#00FF87', color: '#000' }}>
          Wallet address copied!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceivePage;